import { useState, useEffect } from 'react';
import { useProfile } from './useProfile';
import { Partner, PartnerSummaryMetrics, PartnerMetrics } from '@/types/partner';
import { 
  getPartnerMetrics, 
  getPartnerSummaryMetrics, 
  getSinglePartnerMetrics 
} from '@/lib/partners';
import { toast } from 'sonner';

export function usePartnerMetrics(partnerId?: string) {
  const { userProfile } = useProfile();
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [summaryMetrics, setSummaryMetrics] = useState<PartnerSummaryMetrics>({
    total_partners: 0,
    active_partners: 0,
    total_earnings: 0,
    pending_payments: 0,
    total_referrals: 0,
    conversion_rate: 0
  });
  const [singlePartnerMetrics, setSinglePartnerMetrics] = useState<PartnerMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userProfile?.id) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Cargar lista de partners y métricas generales
        const [partnersData, summaryData] = await Promise.all([
          getPartnerMetrics(userProfile.id),
          getPartnerSummaryMetrics(userProfile.id)
        ]);

        setPartners(partnersData);
        setSummaryMetrics(summaryData);

        // Si se especificó un partnerId, cargar sus métricas específicas
        if (partnerId) {
          const partnerMetrics = await getSinglePartnerMetrics(userProfile.id, partnerId);
          setSinglePartnerMetrics(partnerMetrics);
        }
      } catch (err) {
        console.error('Error loading partner metrics:', err);
        setError('Error al cargar las métricas de socios');
        toast.error('Error al cargar las métricas de socios');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userProfile?.id, partnerId]);

  const refreshMetrics = async () => {
    if (!userProfile?.id) return;
    
    try {
      setLoading(true);
      const [partnersData, summaryData] = await Promise.all([
        getPartnerMetrics(userProfile.id),
        getPartnerSummaryMetrics(userProfile.id)
      ]);

      setPartners(partnersData);
      setSummaryMetrics(summaryData);

      if (partnerId) {
        const partnerMetrics = await getSinglePartnerMetrics(userProfile.id, partnerId);
        setSinglePartnerMetrics(partnerMetrics);
      }

      toast.success('Métricas actualizadas correctamente');
    } catch (err) {
      console.error('Error refreshing metrics:', err);
      toast.error('Error al actualizar las métricas');
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    partners,
    summaryMetrics,
    singlePartnerMetrics,
    refreshMetrics
  };
}
